import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import {
  BreadcrumbsViewModel,
  CatalogCategoryViewModel,
  PageViewModel,
  SortTypeEnum,
} from "src/domain/client";
import IProductListReport from "src/domain/event-emitters/product-list-report";
import { ActivityLogService } from "src/services/activity-log.service";
import { BrandService } from "src/services/brand.service";
import { BreadcrumbsService } from "src/services/breadcrumbs.service";
import { CatalogCategoryService } from "src/services/catalog-category.service";
import { ChannelRequestService } from "src/services/channel-request.service";
import { ErrorReporterService } from "src/services/error-reporter.service";
import { ModelAndDealerService } from "src/services/model-and-dealer.service";
import { PageService } from "src/services/page.service";
import { ProductListService } from "src/services/product-list.service";
import { ThemeService } from "src/services/theme.service";

interface RouteParams {
  categoryId?: string;
  slug?: string;
}

@Component({
  selector: "vw-category-page",
  templateUrl: "./category-page.component.html",
})
export class CategoryPageComponent implements OnInit, OnDestroy {
  category?: CatalogCategoryViewModel;
  categoryId?: number;
  page?: PageViewModel;
  breadcrumbs: BreadcrumbsViewModel[] = [];
  totalProductsSum = 0;
  numberOfProductIDsReports = 0;
  hideCategories: { [categoryId: number]: boolean } = {};

  private paramSubscription?: Subscription;
  private channelRequestSubscription?: Subscription;

  constructor(
    public modelAndDealerService: ModelAndDealerService,
    public themeService: ThemeService,
    public channelRequestService: ChannelRequestService,
    private pageService: PageService,
    private errorReporter: ErrorReporterService,
    private brandService: BrandService,
    private route: ActivatedRoute,

    private catalogCategoryService: CatalogCategoryService,
    private breadcrumbService: BreadcrumbsService,
    private productListService: ProductListService,
    private activityLogService: ActivityLogService,
  ) {}

  ngOnInit(): void {
    this.getCategoryPage();

    this.paramSubscription = this.route.params.subscribe(
      (params: Partial<RouteParams>) => {
        this.categoryId =
          params.categoryId != null ? Number(params.categoryId) : undefined;

        if (this.categoryId != null)
          this.getCatalogCategory(this.categoryId, true);
      },
    );

    this.channelRequestSubscription =
      this.channelRequestService.channelRequestUpdated$.subscribe(() => {
        if (this.categoryId != null) {
          this.getCatalogCategory(this.categoryId, false);
        }
      });
  }

  ngOnDestroy(): void {
    this.paramSubscription?.unsubscribe();
    this.channelRequestSubscription?.unsubscribe();
  }

  openSelectDealerModal(): void {
    this.modelAndDealerService.openSelectDealer();
  }

  reportNumberOfProductIDs(event: IProductListReport): void {
    this.totalProductsSum += event.numberOfProducts;

    if (event.categoryId) {
      this.hideCategories[event.categoryId] = event.numberOfProducts === 0;
    }

    this.numberOfProductIDsReports++;
  }

  sortChanged(sortType: SortTypeEnum): void {
    this.productListService.setSortType(sortType);
  }

  //////////

  private getCategoryPage(): void {
    this.pageService
      .getCategoryPage(this.brandService.brandId)
      .then((page) => {
        this.page = page;
      })
      .catch(this.errorReporter.handle);
  }

  private getCatalogCategory(categoryId: number, log = false): void {
    this.totalProductsSum = 0;
    this.numberOfProductIDsReports = 0;
    this.category = undefined;
    this.hideCategories = {};

    const channelRequest = this.channelRequestService.channelRequest;
    this.catalogCategoryService
      .getCatalogCategory(categoryId, channelRequest?.channelRequestId)
      .then((catalogCategoryViewModel) => {
        this.category = catalogCategoryViewModel;

        if (
          log &&
          this.channelRequestService.channelRequest?.channelRequestId
        ) {
          this.activityLogService
            .logCatalogCategoryVisit(
              this.channelRequestService.channelRequest.channelRequestId,
              categoryId,
            )
            .catch(() => {});
        }
        if (catalogCategoryViewModel.catalogCategoryId)
          this.getBreadcrumbs(catalogCategoryViewModel.catalogCategoryId);
      })
      .catch(this.errorReporter.handle);
  }

  private getBreadcrumbs(categoryId: number): void {
    this.breadcrumbService
      .getCategoryHierarchyByCategoryId(categoryId)
      .then((breadcrumbs) => {
        this.breadcrumbs = breadcrumbs;
      })
      .catch(() => {});
  }
}
